import { useCallback, useState } from 'react';
import { ErrorObject } from '../../../utils/validateUtils';
import { EditorModus } from '../EditDialog/EditDialog';
import { EntityProp } from './AdminDataGrid';

export interface SaveDatatGridEditPanelProps<T extends { id: number | string }> extends EntityProp<T> {
  isSaveProps: boolean;
  save: (val: T, mode: EditorModus) => void | Promise<void>;
  getId?: (val: T) => string | number;
  editMode: EditorModus;
  closeEditPanel: () => void;
  searchItemCallback: (q: string | T) => void;
  navEdit: (id: string | number) => void;
}


export const useSaveDataGridEditPanel = <T extends { id: number | string }>(
  {
    isSaveProps,
    save,
    searchEntity,
    updateEntity,
    searchItemCallback,
    editMode,
    createEntity,
    closeEditPanel,
    navEdit,
    getId,
  }: SaveDatatGridEditPanelProps<T>) => {

  const [responseValue, setResponseValue] = useState<T | null>();
  const [responseObjectError, setResponseObjectError] = useState<ErrorObject | null>();

  const saveChangesEditPanel = useCallback(
    async function saveChangesEditPanel(val?: T, keepForm?: boolean) {
      keepForm = keepForm ?? true;
      if (val) {
        if (isSaveProps) {
          save(val, editMode);
        } else {
          switch (editMode) {
            case EditorModus.Soek:
              searchEntity?.(val) || searchItemCallback(val);
              closeEditPanel();
              break;
            case EditorModus.Opprett:
            case EditorModus.OpprettBarn:
            case EditorModus.Kopi:
              {
                const response = createEntity(val, editMode);
                if (keepForm)
                  Promise.resolve(response).then((val) => {
                    const createId = getId?.(val?.data) ?? val?.data?.id;
                    if (createId) navEdit(createId);
                  });
              }
              break;
            case EditorModus.Endre: {
              const response = updateEntity(val);

              Promise.resolve(response).then((res) => {
                if (res.error) {
                  const data = res.error.data;
                  if (!data)
                    return;
                  const properties = Object.keys(data);
                  let error: ErrorObject = {};
                  properties.forEach(propertyName => {
                    if (propertyName in val)
                      error[propertyName] = (data[propertyName] as string[])?.join('\n');
                  });
                  setResponseObjectError(error);
                  return;
                }
                const savedId = getId?.(res?.data) ?? res.data?.id;
                if (savedId && keepForm) {
                  navEdit(savedId);
                  setResponseValue(res.data as T);
                }
              });
              break;
            }
          }
        }
      }
    },
    [isSaveProps, save, editMode, searchEntity, searchItemCallback, closeEditPanel, createEntity, getId, navEdit, updateEntity]
  );
  return { saveChangesEditPanel, responseValue, responseObjectError };
};